import classNames from 'classnames';
import React, { useCallback, useMemo } from 'react';

import { AuthProtectedButton, Button } from 'common/components';

import { useBowser, useCustomer, useI18n } from 'common/hooks';
import { postLogout } from 'common/redux/user/api';

import AuthButtonsComponent from 'components/AuthButtons/AuthButtons.component';

import { routes } from 'routes/routes.constants';

import { memoizedComponent } from 'utils/memoizeComponent';

import { UserCard } from '../UserCard';
import { UserTier } from '../UserTier';
import { SidebarLink } from './SidebarLink';
import { getSidebarItems } from './sidebar.constants';
import './sidebar.css';

type SidebarProps = {
  top?: boolean;
  bottom?: boolean;
  className?: string;
};

const SidebarComponent: React.FC<React.PropsWithChildren<SidebarProps>> = memoizedComponent(
  ({ top = true, bottom, className = '' }) => {
    const { isMobile, isDesktop } = useBowser();
    const { isAuthenticated } = useCustomer();
    const { translate } = useI18n();

    const classesSidebar = classNames('sidebar sidebar-scrollable', {
      [className]: className,
    });

    const sideBarItems = useMemo(
      () => getSidebarItems({ isAuthenticated, isDesktop, isMobile }),
      [isAuthenticated, isDesktop, isMobile],
    );

    const onSignOut = useCallback(() => {
      postLogout().then(() => window.location.assign(routes.getWebsite()));
    }, []);

    return (
      <div data-test='sidebar' className={classesSidebar}>
        {isMobile && <UserCard />}
        {isMobile && <UserTier />}
        <div data-test='sidebar-list' className='sidebar__list'>
          {top && sideBarItems.top.map((item) => <SidebarLink key={item.label} {...item} />)}
          {bottom && sideBarItems.bottom.map((item) => <SidebarLink key={item.label} {...item} />)}
        </div>
        {isMobile && (
          <div className='sidebar__btns'>
            {!isAuthenticated && <p className='sidebar__btns-title'>{translate('mobile:menu:label:guest.message')}</p>}
            <AuthButtonsComponent auth className='sidebar__btns-wrapper' />
            {isAuthenticated && (
              <AuthProtectedButton>
                <Button outline stretch large onClick={onSignOut}>
                  {translate('mobile:signout:title')}
                </Button>
              </AuthProtectedButton>
            )}
          </div>
        )}
      </div>
    );
  },
);

export const Sidebar = SidebarComponent;

export default SidebarComponent;
